import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, RefreshCw, GripVertical, FileText,
  CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronUp,
  Type, Calendar, Clock, List, Mail, Phone, Hash, AlignLeft
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogDescription
} from './ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from './ui/select';
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { Alert, AlertDescription } from './ui/alert';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'textarea', label: 'Text Area', icon: AlignLeft },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'time', label: 'Time', icon: Clock },
  { value: 'select', label: 'Dropdown', icon: List },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'number', label: 'Number', icon: Hash },
];

const AVAILABLE_INTENTS = [
  { value: 'booking', label: 'Booking/Appointment' },
  { value: 'appointment', label: 'Appointment Request' },
  { value: 'sales', label: 'Sales Inquiry' },
  { value: 'support', label: 'Support Request' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'inquiry', label: 'General Inquiry' },
  { value: 'pricing_negotiation', label: 'Pricing Negotiation' },
  { value: 'refund_request', label: 'Refund Request' },
  { value: 'cancellation', label: 'Cancellation' },
  { value: 'custom_quote', label: 'Custom Quote' },
  { value: 'bulk_order', label: 'Bulk Order' },
];

const IntakeTemplateBuilder = ({ channelId, channels, onTemplateChange }) => {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedTemplates, setExpandedTemplates] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    template_name: '',
    trigger_intents: [],
    fields: [],
    channel_id: channelId
  });

  useEffect(() => {
    fetchTemplates();
  }, [channelId]);

  const fetchTemplates = async () => {
    try {
      const url = channelId 
        ? `${API}/intake-templates?channel_id=${channelId}`
        : `${API}/intake-templates`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (formData.fields.length === 0) {
      toast.error('Please add at least one field');
      return;
    }
    if (formData.trigger_intents.length === 0) {
      toast.error('Please select at least one trigger intent');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/intake-templates`, {
        ...formData,
        channel_id: channelId || formData.channel_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Intake template created');
      setIsCreateDialogOpen(false);
      resetForm();
      fetchTemplates();
      onTemplateChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/intake-templates/${selectedTemplate.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template updated');
      setIsEditDialogOpen(false);
      setSelectedTemplate(null);
      resetForm();
      fetchTemplates();
      onTemplateChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    try {
      await axios.delete(`${API}/intake-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      fetchTemplates();
      onTemplateChange?.();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await axios.put(`${API}/intake-templates/${template.id}`, {
        is_active: !template.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Template ${template.is_active ? 'deactivated' : 'activated'}`);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setFormData({
      template_name: template.template_name || '',
      trigger_intents: template.trigger_intents || [],
      fields: template.fields || [],
      channel_id: template.channel_id
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      template_name: '',
      trigger_intents: [],
      fields: [],
      channel_id: channelId
    });
  };

  const addField = () => {
    setFormData({
      ...formData,
      fields: [
        ...formData.fields,
        {
          key: `field_${formData.fields.length}`,
          label: '',
          type: 'text',
          required: true,
          placeholder: '',
          options: []
        }
      ]
    });
  };

  const updateField = (index, updates) => {
    const newFields = [...formData.fields];
    newFields[index] = { ...newFields[index], ...updates };
    setFormData({ ...formData, fields: newFields });
  };

  const removeField = (index) => {
    const newFields = formData.fields.filter((_, i) => i !== index);
    setFormData({ ...formData, fields: newFields });
  };

  const moveField = (index, direction) => {
    const newFields = [...formData.fields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newFields.length) return;
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setFormData({ ...formData, fields: newFields });
  };

  const toggleIntent = (intent) => {
    const intents = formData.trigger_intents.includes(intent)
      ? formData.trigger_intents.filter(i => i !== intent)
      : [...formData.trigger_intents, intent];
    setFormData({ ...formData, trigger_intents: intents });
  };

  const toggleExpanded = (templateId) => {
    setExpandedTemplates(prev => ({
      ...prev,
      [templateId]: !prev[templateId]
    }));
  };

  const getChannelName = (id) => {
    const channel = channels?.find(c => c.id === id);
    return channel?.display_name || 'Unknown';
  };

  // Memoized field editor to prevent re-renders on parent state changes
  const FieldEditor = React.memo(({ field, index, onUpdate, onMove, onRemove, totalFields }) => {
    const FieldIcon = FIELD_TYPES.find(t => t.value === field.type)?.icon || Type;
    
    // Local state for label input to prevent focus loss
    const [localLabel, setLocalLabel] = React.useState(field.label);
    const [localOptions, setLocalOptions] = React.useState(field.options?.join(', ') || '');
    
    // Sync local state when field prop changes (e.g., after reorder)
    React.useEffect(() => {
      setLocalLabel(field.label);
      setLocalOptions(field.options?.join(', ') || '');
    }, [field.key]); // Only sync when field key changes (different field)
    
    const handleLabelBlur = () => {
      if (localLabel !== field.label) {
        onUpdate(index, { 
          label: localLabel,
          key: localLabel.toLowerCase().replace(/\s+/g, '_') || field.key
        });
      }
    };
    
    const handleOptionsBlur = () => {
      const newOptions = localOptions.split(',').map(o => o.trim()).filter(o => o);
      onUpdate(index, { options: newOptions });
    };
    
    return (
      <div className="border rounded-lg p-4 bg-gray-50" data-testid={`field-${index}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />
            <FieldIcon className="h-4 w-4 text-purple-500" />
            <span className="font-medium text-sm">{localLabel || `Field ${index + 1}`}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onMove(index, 'up')}
              disabled={index === 0}
              type="button"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onMove(index, 'down')}
              disabled={index === totalFields - 1}
              type="button"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500"
              onClick={() => onRemove(index)}
              type="button"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Field Label</Label>
            <Input
              placeholder="e.g., Full Name"
              value={localLabel}
              onChange={(e) => setLocalLabel(e.target.value)}
              onBlur={handleLabelBlur}
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Field Type</Label>
            <Select 
              value={field.type} 
              onValueChange={(v) => onUpdate(index, { type: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>
                    <div className="flex items-center gap-2">
                      <t.icon className="h-4 w-4" />
                      {t.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {field.type === 'select' && (
          <div className="mt-3 space-y-1">
            <Label className="text-xs">Options (comma-separated)</Label>
            <Input
              placeholder="Option 1, Option 2, Option 3"
              value={localOptions}
              onChange={(e) => setLocalOptions(e.target.value)}
              onBlur={handleOptionsBlur}
            />
          </div>
        )}

        <div className="mt-3 flex items-center gap-2">
          <Switch
            checked={field.required}
            onCheckedChange={(v) => onUpdate(index, { required: v })}
          />
          <Label className="text-xs text-gray-600">Required field</Label>
        </div>
      </div>
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <RefreshCw className="h-6 w-6 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="intake-template-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Structured Intake Templates</h3>
          <p className="text-sm text-gray-500">
            AI will collect required information before escalating to your team
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-template-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Intake Template</DialogTitle>
              <DialogDescription>
                Define what information AI should collect from customers
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Template Name</Label>
                <Input
                  placeholder="e.g., Appointment Booking Form"
                  value={formData.template_name}
                  onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                  required
                />
              </div>

              {!channelId && channels && channels.length > 0 && (
                <div className="space-y-2">
                  <Label>Channel</Label>
                  <Select 
                    value={formData.channel_id || ''} 
                    onValueChange={(v) => setFormData({ ...formData, channel_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a channel" />
                    </SelectTrigger>
                    <SelectContent>
                      {channels.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Trigger Intents</Label>
                <p className="text-xs text-gray-500 mb-2">
                  When AI detects these intents, it will start collecting information
                </p>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_INTENTS.map(intent => (
                    <Badge
                      key={intent.value}
                      variant={formData.trigger_intents.includes(intent.value) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleIntent(intent.value)}
                    >
                      {formData.trigger_intents.includes(intent.value) && (
                        <CheckCircle className="h-3 w-3 mr-1" />
                      )}
                      {intent.label}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Information Fields</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addField}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Field
                  </Button>
                </div>

                {formData.fields.length === 0 ? (
                  <Alert>
                    <AlertDescription className="text-center py-4">
                      No fields yet. Add fields to define what information to collect.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-3">
                    {formData.fields.map((field, index) => (
                      <FieldEditor 
                        key={field.key || `field-${index}`} 
                        field={field} 
                        index={index}
                        onUpdate={updateField}
                        onMove={moveField}
                        onRemove={removeField}
                        totalFields={formData.fields.length}
                      />
                    ))}
                  </div>
                )}
              </div>

              <Alert className="bg-gray-50">
                <AlertDescription className="text-sm text-gray-600">
                  AI will ask the customer for any missing required information before escalating to your team.
                </AlertDescription>
              </Alert>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                  Create Template
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Templates List */}
      {templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <FileText className="h-10 w-10 text-gray-300 mb-3" />
            <h4 className="font-medium text-gray-900 mb-1">No intake templates</h4>
            <p className="text-sm text-gray-500 text-center mb-3">
              Create a template to have AI collect structured information
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {templates.map((template) => (
            <Collapsible 
              key={template.id}
              open={expandedTemplates[template.id]}
              onOpenChange={() => toggleExpanded(template.id)}
            >
              <Card 
                data-testid={`template-${template.id}`}
                className={!template.is_active ? 'opacity-75 bg-gray-50' : ''}
              >
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between">
                    <CollapsibleTrigger className="flex items-center gap-3 hover:opacity-80">
                      <FileText className={`h-5 w-5 ${template.is_active ? 'text-purple-500' : 'text-gray-400'}`} />
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <CardTitle className={`text-base ${!template.is_active ? 'text-gray-500' : ''}`}>
                            {template.template_name}
                          </CardTitle>
                          {!template.is_active && (
                            <Badge variant="outline" className="text-xs bg-gray-100 text-gray-500 border-gray-300">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <CardDescription className="text-xs">
                          {template.fields?.length || 0} fields • {template.trigger_intents?.length || 0} triggers
                        </CardDescription>
                      </div>
                      {expandedTemplates[template.id] ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </CollapsibleTrigger>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={template.is_active}
                        onCheckedChange={() => handleToggleActive(template)}
                        data-testid={`toggle-${template.id}`}
                      />
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(template)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500"
                        onClick={() => handleDelete(template.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent className="pt-0 border-t">
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <Label className="text-xs text-gray-500">Trigger Intents</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {template.trigger_intents?.map(intent => (
                            <Badge key={intent} variant="outline" className="text-xs">
                              {AVAILABLE_INTENTS.find(i => i.value === intent)?.label || intent}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Fields</Label>
                        <div className="space-y-1 mt-1">
                          {template.fields?.map((field, idx) => (
                            <div key={idx} className="text-sm flex items-center gap-1">
                              <span className={field.required ? 'text-red-500' : 'text-gray-400'}>
                                {field.required ? '*' : '○'}
                              </span>
                              {field.label}
                              <Badge variant="secondary" className="text-xs ml-1">
                                {field.type}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={formData.template_name}
                onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Trigger Intents</Label>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_INTENTS.map(intent => (
                  <Badge
                    key={intent.value}
                    variant={formData.trigger_intents.includes(intent.value) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleIntent(intent.value)}
                  >
                    {formData.trigger_intents.includes(intent.value) && (
                      <CheckCircle className="h-3 w-3 mr-1" />
                    )}
                    {intent.label}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Information Fields</Label>
                <Button type="button" variant="outline" size="sm" onClick={addField}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Field
                </Button>
              </div>

              <div className="space-y-3">
                {formData.fields.map((field, index) => (
                  <FieldEditor 
                    key={field.key || `edit-field-${index}`} 
                    field={field} 
                    index={index}
                    onUpdate={updateField}
                    onMove={moveField}
                    onRemove={removeField}
                    totalFields={formData.fields.length}
                  />
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntakeTemplateBuilder;
